import { Option } from "@campgladiator/cgui-core.atoms.dropdown";
import { parseMemberships } from "@campgladiator/cg-common.logic.memberships";
import type {
  ProductDTO,
  UpgradeType,
  SubscriptionDTO,
  Role,
} from "@campgladiator/cg-common.types.types";
import { useCallback, useEffect, useState } from "react";
import useSelectCouponInput from "../../../SelectCouponInput/useSelectCouponInput";

export type SelectedProductOption = {
  newProductId: string;
  newProductPriceId: string;
  productDetails: {
    [key: string]: any;
  };
};

type UseSelectProductProps = {
  membership: ProductDTO[];
  upgradeType: UpgradeType;
  currentSubscription: SubscriptionDTO;
  role?: Role;
};

const useSelectProduct = ({
  membership,
  upgradeType,
  currentSubscription,
  role,
}: UseSelectProductProps) => {
  const [selectedProductOption, setSelectedProductOption] =
    useState<SelectedProductOption | null>(null);
  const [productOptions, setProductOptions] = useState<Option[]>([]);

  const { couponOptions, handleOnSelectCoupon, selectedCouponOption } =
    useSelectCouponInput({
      selectedProductId: selectedProductOption?.productDetails?.id,
      role,
    });

  const handleOnSelectNewMembership = (option?: Option) => {
    if (option) {
      const [newProductId, newProductPriceId] = option.itemId.split("|");
      if (newProductId && newProductPriceId)
        setSelectedProductOption({
          newProductId,
          newProductPriceId,
          productDetails: option.metadata || {},
        });
    } else setSelectedProductOption(null);
  };

  const getProductDropdownOptions = useCallback(() => {
    const dropdownOptions = parseMemberships(
      membership,
      upgradeType,
      currentSubscription
    );
    setProductOptions(dropdownOptions);
    setSelectedProductDropdownOptions(dropdownOptions);
  }, [membership, upgradeType, currentSubscription]);

  const defaultToSameTermLengthOption = (dropdownOptions: Option[]) => {
    const currentProductOption = dropdownOptions.find(
      (product) =>
        product.metadata.termLength === currentSubscription.termLength
    );
    if (currentProductOption) handleOnSelectNewMembership(currentProductOption);
  };

  const defaultToSingularOption = (dropdownOptions: Option[]) => {
    if (dropdownOptions.length === 1) {
      handleOnSelectNewMembership(dropdownOptions[0]);
    } else {
      setSelectedProductOption(undefined);
    }
  };

  const setSelectedProductDropdownOptions = (dropdownOptions: Option[]) => {
    if (upgradeType === "UPGRADE") {
      defaultToSameTermLengthOption(dropdownOptions);
    } else {
      defaultToSingularOption(dropdownOptions);
    }
  };

  useEffect(() => {
    getProductDropdownOptions();
  }, [membership]);

  return {
    couponOptions,
    productOptions,
    selectedProductOption,
    setSelectedProductOption,
    handleOnSelectNewMembership,
    handleOnSelectCoupon,
    selectedCouponOption,
  };
};

export default useSelectProduct;
